import { CheckIcon } from '../assets';
import { Tag } from 'antd';
import { LOCATION_TYPES } from '../../constants';

interface ListManagerProps<T extends { uuid: string; locationType?: string }> {
  addedItems: T[];
  availableItems: T[];
  onRemoveItem: (item: T) => void;
  onAddItem: (item: T) => void;
  addedTitle: string;
  availableTitle: string;
  getItemLabel: (item: T) => string;
  extendItemOptions?: (item: T) => React.ReactNode;
  infoContent?: React.ReactNode;
}

const ListManager = <
  T extends { uuid: string; locationType?: string | undefined },
>({
  addedItems,
  availableItems,
  onRemoveItem,
  onAddItem,
  addedTitle,
  availableTitle,
  getItemLabel,
  extendItemOptions,
  infoContent,
}: ListManagerProps<T>) => {
  return (
    <div className="flex flex-col w-full">
      {/* Title Row */}
      <div className="flex w-full justify-between border-b-2 border-dotted">
        <div className="w-1/2 flex justify-center">
          <div className="w-1/2 text-center bg-blue text-white rounded-t-xl py-1 font-bold uppercase">
            {addedTitle}
          </div>
        </div>
        <div className="w-1/2 flex justify-center">
          <div className="w-1/2 text-center bg-blue text-white rounded-t-xl py-1 font-bold uppercase">
            {availableTitle}
          </div>
        </div>
      </div>

      {/* Item Lists */}
      <div className="flex w-full justify-between px-8 mt-8">
        <div className="w-1/2">
          {/* Added Items */}
          <div className=" flex flex-col items-center max-h-96 overflow-y-auto">
            {addedItems?.length > 0 ? (
              addedItems?.map((item) => {
                const isDistributionCenter =
                  item.locationType === LOCATION_TYPES.DISTRIBUTION_CENTER;

                const extendedOptions = extendItemOptions?.(item);
                return (
                  <button
                    key={item.uuid}
                    className="w-3/4 flex border border-blue bg-sky-200 rounded-xl justify-between items-center mb-2 px-4 py-2 cursor-pointer"
                    onClick={() => onRemoveItem(item)}
                  >
                    <div className="flex items-center">
                      <span className="font-bold">{getItemLabel(item)}</span>
                      {isDistributionCenter && (
                        <div className="ml-3">
                          <Tag color="green">Distribution Center</Tag>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="border border-blue bg-blue text-white rounded-full w-7 h-7 flex items-center justify-center">
                        <CheckIcon className="fill-white" />
                      </span>
                      {extendedOptions}
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="placeholder-text">No elements found.</div>
            )}
          </div>
          {/* Info Text */}
          {infoContent && infoContent}
        </div>

        {/* Available Items */}
        <div className="w-1/2 flex flex-col items-center max-h-96 overflow-y-auto">
          {availableItems?.length > 0 ? (
            availableItems?.map((item) => {
              const isDistributionCenter =
                item.locationType === LOCATION_TYPES.DISTRIBUTION_CENTER;

              const extendedOptions = extendItemOptions?.(item);
              return (
                <button
                  key={item.uuid}
                  className="w-3/4 flex border rounded-xl justify-between items-center mb-2 px-4 py-2 hover:border-blue hover:bg-sky-200 cursor-pointer"
                  onClick={() => onAddItem(item)}
                >
                  <div className="flex items-center">
                    <span>{getItemLabel(item)}</span>
                    {isDistributionCenter && (
                      <div className="ml-3">
                        <Tag color="green">Distribution Center</Tag>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="border border-blue rounded-full w-7 h-7 flex items-center justify-center">
                      <CheckIcon />
                    </span>
                    {extendedOptions}
                  </div>
                </button>
              );
            })
          ) : (
            <div className="placeholder-text">No elements found.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListManager;
